import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState, LoadingRows } from '../../components/ui';
import { DeploymentHistory } from '../../components/deploy/DeploymentHistory';
import { PanelCard, ProjectSection, QuietEmpty, SectionHeader } from '../../components/project/ui';
import { Callout } from '../../components/primitives';
import { api } from '../../api';
import type { AttentionDismissResult, DismissedAttentionItem, EnvironmentHealthView } from '../../api';
import { useResource } from '../../hooks';
import type { Resource } from '../../hooks';
import type { ReadinessState } from '../../components/EnvironmentReadiness';
import type { AttentionItem, ProjectHealth, SetupProgress } from '../../components/project/projectHealth';
import { relativeTime } from '../../components/project/projectHealth';
import { useProject } from './ProjectContext';
import { ComponentStatusPanel } from '../../components/project/ComponentStatusPanel';
import { StatusBand } from '../../components/project/StatusBand';
import { summariseReadiness } from '../../components/project/statusStrip';
import { actionLabel } from '../../components/operation/actionLabels';
import { AttentionFeed } from '../../components/status/AttentionFeed';
import { groupByCategory } from '../../components/status/statusModel';
import { SSHEnvironmentNotice, isOperatedOverSSH } from '../../components/project/SSHEnvironmentNotice';

/**
 * OverviewSection: a compact status strip (site, last deploy, infrastructure,
 * backup), what needs attention — only when something does — what each
 * component serves, and the latest deployments. Healthy is quiet: no banner
 * announces that nothing is wrong; the absence of the attention area is the
 * signal. Every item links to where the depth is.
 */
export function OverviewSection({
  environmentHealth,
  attention,
  setup,
  readiness,
}: {
  /** The header's level; the Overview reads the dimensions instead. */
  health?: ProjectHealth;
  /** The server's derivation for this environment (CO-95, CO-150). */
  environmentHealth: Resource<EnvironmentHealthView | null>;
  attention: AttentionItem[];
  setup: SetupProgress;
  readiness: ReadinessState;
}) {
  const project = useProject();
  const { environment, sectionPath, deployments, components } = project;

  if (!environment) {
    return (
      <ProjectSection>
        <EmptyState title="No environments">
          <Link className="button button--primary" to={sectionPath('settings')}>
            Add an environment
          </Link>
        </EmptyState>
      </ProjectSection>
    );
  }

  const deploymentList = deployments.data?.deployments ?? [];
  const derived = environmentHealth.data;
  const healthLoading = environmentHealth.loading && !derived;
  const overSSH = isOperatedOverSSH(environment, project.isWordPress);
  const readinessSummary = setup.complete && !overSSH && readiness.rows.length > 0 ? summariseReadiness(readiness.rows, readiness.inspecting) : null;

  const attentionPanel = (
    <AttentionPanel
      items={attention}
      loading={healthLoading}
      error={environmentHealth.error}
      onRetry={environmentHealth.reload}
      dismissal={{ environmentId: environment.id, dismissed: derived?.dismissed ?? [], onChanged: environmentHealth.reload }}
    />
  );

  return (
    <ProjectSection>
      {overSSH ? <SSHEnvironmentNotice environment={environment} what="Deployments, backups, cron and queues" /> : null}
      {!setup.complete ? <SetupBanner setup={setup} to={sectionPath('setup')} /> : null}

      <StatusBand
        health={derived}
        loading={healthLoading}
        error={environmentHealth.error}
        onRetry={environmentHealth.reload}
        sectionPath={sectionPath}
        readiness={readinessSummary}
        deploymentsElsewhere={overSSH}
      />

      {/* Problems come before the working content (CO-161). */}
      {attentionPanel}

      {setup.complete && !overSSH ? (
        <ComponentStatusPanel
          states={derived?.components}
          views={components.data?.components ?? []}
          loading={healthLoading}
          deploymentPath={(id) => sectionPath('deployments', { deployment: id })}
        />
      ) : null}

      {/* History stays last and short; the Deployments section has it all. */}
      <PanelCard labelledBy="overview-deployments" testId="recent-deployments">
        <SectionHeader
          id="overview-deployments"
          title="Recent deployments"
          actions={
            <Link className="button button--secondary button--small" to={sectionPath('deployments')}>
              All deployments
            </Link>
          }
        />
        {deployments.loading && deploymentList.length === 0 ? (
          <LoadingRows label="Loading deployments" rows={3} rowHeight="48px" />
        ) : deploymentList.length === 0 ? (
          <QuietEmpty testId="no-deployments">
            {overSSH ? (
              'Listed on the environment page'
            ) : (
              <>
                No deployments · <Link to={sectionPath('recovery')}>Import a store</Link>
              </>
            )}
          </QuietEmpty>
        ) : (
          <DeploymentHistory
            deployments={deploymentList.slice(0, 3)}
            serving={deployments.data?.serving}
            variant="compact"
            deploymentPath={(id) => sectionPath('deployments', { deployment: id })}
            serverName={project.serverName}
            repositoryOf={(deployment) => project.applications.find((app) => app.id === deployment.application_id)?.repository_url || project.project.repository_url}
          />
        )}
      </PanelCard>

    </ProjectSection>
  );
}

/**
 * SetupBanner: setup progress and the way back into the flow, as one line.
 * It disappears once the environment runs on a server.
 */
function SetupBanner({ setup, to }: { setup: SetupProgress; to: string }) {
  // The plan is a persisted record (CO-73): the run state is a status word,
  // and the action goes straight to what needs doing.
  const plan: { state: string; action: string } | null =
    setup.planLifecycle === 'approved'
      ? { state: 'Plan approved', action: 'Review and run the plan' }
      : setup.planLifecycle === 'run_pending'
        ? { state: 'Run not started', action: 'Open the run' }
        : setup.planLifecycle === 'running'
          ? { state: 'Running', action: 'Follow the run' }
          : setup.planLifecycle === 'failed'
            ? { state: 'Run failed', action: 'Review the plan' }
            : setup.planLifecycle === 'cancelled'
              ? { state: 'Run cancelled', action: 'Review the plan' }
              : setup.planLifecycle === 'awaiting_approval'
                ? { state: 'Awaiting approval', action: 'Review the plan' }
                : null;
  return (
    <div className="setup-banner" data-testid="setup-banner" data-lifecycle={setup.planLifecycle ?? ''}>
      <div>
        <strong>
          Setup · {setup.done}/{setup.total}
        </strong>
        {plan ? <span className="setup-banner__next">{plan.state}</span> : setup.next ? <span className="setup-banner__next">Next · {setup.next}</span> : null}
      </div>
      <Link className="button button--primary" to={to}>
        {plan?.action ?? 'Continue setup'}
      </Link>
    </div>
  );
}

/**
 * AttentionPanel is the only place on the Overview that is allowed to be
 * loud, and only when something is actually wrong. With nothing to report
 * it renders nothing: no banner announces that all is well. Unknown readings
 * are shown by the status strip, not here.
 */
export function AttentionPanel({
  items,
  loading,
  error,
  onRetry,
  dismissal,
}: {
  items: AttentionItem[];
  loading: boolean;
  error?: Error | null;
  onRetry?: () => void;
  /** Bulk dismissal of advisory items (observation 5); omitted where not offered. */
  dismissal?: { environmentId: string; dismissed: DismissedAttentionItem[]; onChanged: () => void };
}) {
  // The server re-checks every dismissal (and refuses critical items); this
  // only decides whether the controls are offered.
  const workspace = useResource(api.workspace, [], undefined, { key: 'workspace' });
  const identity = workspace.data?.identity;
  const canDismiss = Boolean(
    dismissal && (identity?.role?.is_owner_role || identity?.capabilities?.includes('manage_monitoring') || identity?.kind === 'local_development_workspace'),
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'warning'; text: string } | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const dismissible = useMemo(() => items.filter((item) => item.dismissible), [items]);
  // A selection only ever holds items still listed.
  const selectedNow = useMemo(() => new Set([...selected].filter((key) => dismissible.some((item) => item.key === key))), [selected, dismissible]);

  const toggle = (key: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const dismiss = async (keys: string[]) => {
    if (!dismissal || keys.length === 0) return;
    setBusy(true);
    setFeedback(null);
    try {
      const chosen = dismissible.filter((item) => keys.includes(item.key));
      const result: AttentionDismissResult = await api.dismissAttention(
        dismissal.environmentId,
        chosen.map((item) => ({ id: item.key, fingerprint: item.fingerprint })),
      );
      setSelected(new Set());
      const done = result.dismissed.length;
      setFeedback(
        result.refused.length > 0
          ? { tone: 'warning', text: `Dismissed ${done} · Not dismissed: ${result.refused.map((r) => r.message).join(' ')}` }
          : { tone: 'success', text: `Dismissed ${done}` },
      );
      dismissal.onChanged();
    } catch (cause) {
      setFeedback({ tone: 'warning', text: cause instanceof Error ? cause.message : String(cause) });
    } finally {
      setBusy(false);
    }
  };

  const restore = async (ids: string[]) => {
    if (!dismissal) return;
    setBusy(true);
    setFeedback(null);
    try {
      const result = await api.restoreAttention(dismissal.environmentId, ids);
      setFeedback({ tone: 'success', text: `Showing ${result.restored} again` });
      dismissal.onChanged();
    } catch (cause) {
      setFeedback({ tone: 'warning', text: cause instanceof Error ? cause.message : String(cause) });
    } finally {
      setBusy(false);
    }
  };

  const dismissedList = dismissal?.dismissed ?? [];
  const dismissedBlock =
    dismissedList.length > 0 ? (
      <div className="attention-dismissed" data-testid="attention-dismissed">
        <button type="button" className="link-button" aria-expanded={showDismissed} onClick={() => setShowDismissed((value) => !value)}>
          {showDismissed ? 'Hide' : 'Show'} {dismissedList.length} dismissed {dismissedList.length === 1 ? 'item' : 'items'}
        </button>
        {showDismissed ? (
          <>
            <ul className="attention-dismissed__list">
              {dismissedList.map((item) => (
                <li key={item.id} className="attention-dismissed__item">
                  <span>
                    <strong>{item.title}</strong>
                    <span className="muted">
                      {' '}
                      Dismissed by {item.dismissed_by} {relativeTime(item.dismissed_at)}
                    </span>
                  </span>
                  {canDismiss ? (
                    <button type="button" className="button button--ghost button--small" disabled={busy} onClick={() => restore([item.id])} aria-label={`Show “${item.title}” again`}>
                      Show again
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
            {canDismiss && dismissedList.length > 1 ? (
              <button type="button" className="button button--secondary button--small" disabled={busy} onClick={() => restore([])}>
                Show all again
              </button>
            ) : null}
          </>
        ) : null}
      </div>
    ) : null;
  const feedbackLine = feedback ? (
    <p className={`summary-line attention-feedback attention-feedback--${feedback.tone}`} role="status" data-testid="attention-feedback">
      {feedback.text}
    </p>
  ) : null;

  // A read that failed is not an all-clear: say so, never "Nothing needs
  // attention" (found by the visual suite, CO-96).
  if (error && items.length === 0) {
    return (
      <Callout
        tone="warning"
        title="Attention items unavailable"
        testId="attention-error"
        actions={
          onRetry ? (
            <button type="button" className="button button--secondary button--small" onClick={onRetry}>
              {actionLabel.retry}
            </button>
          ) : undefined
        }
      >
        {error.message}
      </Callout>
    );
  }
  if (loading) {
    return (
      <PanelCard testId="attention-panel">
        <LoadingRows label="Checking this environment" rows={2} rowHeight="32px" />
      </PanelCard>
    );
  }

  if (items.length === 0) {
    return feedbackLine || dismissedBlock ? (
      <div>
        {feedbackLine}
        {dismissedBlock}
      </div>
    ) : null;
  }

  const selectable = canDismiss && dismissible.length > 0;
  const groups = groupByCategory(items.map((item) => ({ ...item, category: item.category, kind: item.kind ?? '' })));

  return (
    <PanelCard labelledBy="attention-title" testId="attention-panel">
      <SectionHeader
        id="attention-title"
        title={`Needs attention (${items.length})`}
        actions={
          selectable ? (
            <div className="attention-toolbar" role="group" aria-label="Dismiss advisory items" data-testid="attention-toolbar">
            <span className="attention-toolbar__count" aria-live="polite">
              {selectedNow.size > 0 ? `${selectedNow.size} selected` : null}
            </span>
            {selectedNow.size < dismissible.length ? (
              <button type="button" className="button button--ghost button--small" disabled={busy} onClick={() => setSelected(new Set(dismissible.map((item) => item.key)))}>
                Select all advisory
              </button>
            ) : null}
            {selectedNow.size > 0 ? (
              <>
                <button type="button" className="button button--ghost button--small" disabled={busy} onClick={() => setSelected(new Set())}>
                  Clear selection
                </button>
                <button
                  type="button"
                  className="button button--secondary button--small"
                  disabled={busy}
                  onClick={() => dismiss([...selectedNow])}
                  data-testid="attention-dismiss-selected"
                >
                  {busy ? 'Dismissing' : `Dismiss selected (${selectedNow.size})`}
                </button>
              </>
            ) : null}
            <button
              type="button"
              className="button button--ghost button--small"
              disabled={busy}
              onClick={() => dismiss(dismissible.map((item) => item.key))}
              title="Dismissed items come back if anything about them changes"
              data-testid="attention-dismiss-all"
            >
              Dismiss all advisory ({dismissible.length})
            </button>
          </div>
          ) : undefined
        }
      />
      {feedbackLine}
      <div className="attention-groups">
        {groups.map((group) => (
          <section key={group.category} className="attention-group" aria-label={group.label} data-category={group.category}>
            <h3 className="attention-group__title">
              {group.label}
              <span className="dash-section__count">{group.items.length}</span>
            </h3>
            <AttentionFeed
              label={group.label}
              limit={selectable ? undefined : 4}
              selection={selectable ? { selected: selectedNow, onToggle: toggle } : undefined}
              items={group.items.map((item) => ({
                key: item.key,
                severity: item.severity,
                title: item.title,
                summary: item.detail,
                when: item.when,
                action: item.action,
                related: item.related,
                dismissible: item.dismissible,
              }))}
            />
          </section>
        ))}
      </div>
      {dismissedBlock}
    </PanelCard>
  );
}
